import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, MapPin, AlertTriangle } from 'lucide-react';
import { LocationsFilters, LocationsTable, LocationForm } from './components';
import { useLocations } from './hooks/useLocations';
import type { Location, CreateLocationData, UpdateLocationData } from './types';

export const LocationsPage: React.FC = () => {
  const {
    locations,
    stats,
    filters,
    loading,
    error,
    createLocation,
    updateLocation,
    deleteLocation,
    toggleLocationStatus,
    updateFilters,
    clearError,
  } = useLocations();

  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Manejar creación de nueva sucursal
  const handleCreateLocation = () => {
    setSelectedLocation(null);
    setFormError(null);
    setIsFormDialogOpen(true);
  };

  // Manejar edición de sucursal
  const handleEditLocation = (location: Location) => {
    setSelectedLocation(location);
    setFormError(null);
    setIsFormDialogOpen(true);
  };

  // Manejar eliminación de sucursal
  const handleDeleteLocation = (location: Location) => {
    setSelectedLocation(location);
    setIsDeleteDialogOpen(true);
  };

  // Confirmar eliminación
  const confirmDeleteLocation = async () => {
    if (!selectedLocation) return;

    try {
      await deleteLocation(selectedLocation.id);
      setIsDeleteDialogOpen(false);
      setSelectedLocation(null);
    } catch (error) {
      console.error('Error al eliminar sucursal:', error);
    }
  };

  // Enviar formulario
  const handleFormSubmit = async (data: CreateLocationData | UpdateLocationData) => {
    setIsFormLoading(true);
    setFormError(null);
    
    try {
      if (selectedLocation && 'id' in data) {
        // Editar sucursal existente
        await updateLocation(data as UpdateLocationData);
      } else {
        // Crear nueva sucursal
        await createLocation(data as CreateLocationData);
      }
      
      // Solo cerrar el modal si no hay errores
      setIsFormDialogOpen(false);
      setSelectedLocation(null);
      setFormError(null);
    } catch (error) {
      // Mantener el modal abierto y mostrar el error
      const errorMessage = error instanceof Error ? error.message : 'Error al guardar sucursal';
      setFormError(errorMessage);
      console.error('Error al guardar sucursal:', error);
    } finally {
      setIsFormLoading(false);
    }
  };

  // Cambiar estado de sucursal
  const handleToggleStatus = async (location: Location, newStatus: Location['estado']) => {
    try {
      await toggleLocationStatus(location.id, newStatus);
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    }
  };

  // Limpiar filtros
  const handleClearFilters = () => {
    updateFilters({
      estado: 'TODOS',
      searchQuery: '',
    });
  };

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="h-8 w-8 text-macartur-primary" />
            Gestión de Sucursales
          </h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">
            Administra todas las ubicaciones de Macartur
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            onClick={handleCreateLocation} 
            className="flex items-center gap-2 bg-macartur-primary hover:bg-macartur-primary/90"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Nueva Sucursal</span>
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div>
                <h3 className="font-medium text-red-900">Error</h3>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={clearError}
                className="ml-auto"
              >
                Cerrar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}


      {/* Filtros */}
      <LocationsFilters
        filters={filters}
        onFiltersChange={updateFilters}
        onClearFilters={handleClearFilters}
      />

      {/* Tabla de sucursales */}
      <div className="bg-white rounded-lg border">
        <LocationsTable
          locations={locations}
          onEdit={handleEditLocation}
          onDelete={handleDeleteLocation}
          onToggleStatus={handleToggleStatus}
          loading={loading}
        />
      </div>

      {/* Dialog para formulario */}
      <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
        <DialogContent className="max-w-[90vw] max-h-[85vh] w-[90vw] min-w-[800px] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedLocation ? 'Editar Sucursal' : 'Nueva Sucursal'}
            </DialogTitle>
          </DialogHeader>
          <LocationForm
            location={selectedLocation || undefined}
            onSubmit={handleFormSubmit}
            onCancel={() => setIsFormDialogOpen(false)}
            isLoading={isFormLoading}
            isEdit={!!selectedLocation}
            error={formError}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              ¿Estás seguro de que deseas eliminar la sucursal{' '}
              <strong>{selectedLocation?.nombre}</strong>?
            </p>
            <p className="text-sm text-red-600">
              Esta acción no se puede deshacer y eliminará todos los datos
              asociados a esta sucursal.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteLocation}
              >
                Eliminar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};